import {
  InputAdornment,
  InputBaseProps,
  SlotProps,
  TextField,
  TextFieldOwnerState,
  TextareaAutosize,
} from "@mui/material";
import { useSignal, useSignalEffect } from "@preact/signals-react";
import IMask from "imask";
import { debounce, isNil, toNumber } from "lodash-es";
import React, { KeyboardEvent, useContext, useRef } from "react";
import NGLayoutItem from "../../generators/NGLayoutItem";
import { setAdornment } from "../../library/adornmentUtil";
import { publishUserAction } from "../../library/analytics";
import { GetFormatFromSite, setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { INGInputFieldProps } from "../../library/NGFieldExtensions";
import {
  createAutocompleteConfigAndContext,
  generateUID,
  getClassName,
  getCustomLabel,
  getTestId,
  getsxObject,
  ignoreCustomHandlers,
  partitionMUIClasses,
} from "../../library/utils";
import NGIcon from "../NGIcon/NGIcon";

const tag = "NGInputField";

export default function NGInputField({ config, context }: INGInputFieldProps) {
  const local = setupLocalState(
    config,
    {
      DefaultValue: useSignal(config.DefaultValue ?? ""),
      Value: useSignal(config.DefaultValue ?? ""),
      Label: useSignal(config.Label ?? ""),
      Visible: useSignal(config.Visible ?? true),
      Multiline: useSignal(config.Multiline ?? false),
      Resize: useSignal(config.Resize ?? "none"),
      MaxRows: useSignal(config.MaxRows ?? null),
      MinRows: useSignal(config.MinRows ?? null),
      Rows: useSignal(config.Rows ?? null),
      Disabled: useSignal(config.Disabled ?? false),
      Classes: useSignal(config.Classes ?? ""),
      Style: useSignal(config.Style ?? {}),
      FocusOnDisplay: useSignal(config.FocusOnDisplay ?? false),
      Debounce: useSignal(config.Debounce ?? 1000),
      InputType: useSignal(config.InputType ?? "text"),
      Valid: useSignal(config.Valid ?? { success: true, reasons: [] }),
      Format: useSignal(config.Format ?? ""),
      ContextAutocomplete: useSignal(config.ContextAutocomplete ?? null),
    },
    context
  );

  const maskedValue = useSignal<string | null>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<InstanceType<typeof IMask.InputMask> | null>(null);
  const handlers = setupHandlers(config, context);
  const handlersKeys = Object.keys(handlers);

  const debouncedHandler = debounce((fn, event, value) => {
    if (!isNil(fn)) {
      fn(event, value);
    }
  }, local.Debounce.value);

  const inputProps: any = {};
  const InputProps: SlotProps<React.ElementType<InputBaseProps>, object, TextFieldOwnerState> = {};

  if (local.InputType.value === "number") {
    inputProps.pattern = "[0-9]*[.,]?[0-9]*";
  }

  if (config.Resize) {
    InputProps.style = { resize: (config.Resize as React.CSSProperties["resize"]) ?? "none" };
  }

  if (config.Multiline && config.MultilineAutosize) {
    InputProps.inputcomponent = TextareaAutosize;
    if (!isNil(config.MinRows)) InputProps.minRows = config.MinRows;
    if (!isNil(config.MaxRows)) InputProps.maxRows = config.MaxRows;
  }

  if (!isNil(config.Min)) inputProps.min = config.Min;
  if (!isNil(config.Max)) inputProps.max = config.Max;
  if (!isNil(config.MaxLength)) inputProps.maxLength = config.MaxLength;

  if (config.Adornment?.Position) {
    const position = config.Adornment.Position.toLowerCase();
    const iconName = config.Adornment.Icon?.IconName ?? "search";
    inputProps[`${position}Adornment`] = (
      <InputAdornment position={position as "start" | "end"}>
        <NGIcon config={{ Id: generateUID(), IconName: iconName }} context={context} />
      </InputAdornment>
    );
  }

  local.InputProps = useSignal(InputProps);
  local.inputProps = useSignal(inputProps);
  local.InDesignMode = useSignal(context?.InDesignMode ?? false);
  setAdornment({
    position: "start",
    adornment: config.StartAdornment,
    inputProps: local.inputProps.value,
    context,
    configId: config.Id,
  });
  setAdornment({
    position: "end",
    adornment: config.EndAdornment,
    inputProps: local.inputProps.value,
    context,
    configId: config.Id,
  });

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  useSignalEffect(() => {
    if (inputRef.current && local.FocusOnDisplay.value) {
      inputRef.current.focus();
    }
  });

  const transformMaskOptions = (options: any) => {
    const resolved = { ...options };
    const typeMapping: { [key: string]: any } = {
      Number: Number,
      Date: Date,
      RegExp: RegExp,
    };

    if (typeof resolved.mask === "string" && typeMapping[resolved.mask]) {
      resolved.mask = typeMapping[resolved.mask];
    }
    if (resolved.blocks) {
      const newBlocks = { ...resolved.blocks };
      Object.keys(newBlocks).forEach((blockKey) => {
        const block = { ...newBlocks[blockKey] };
        if (typeof block.mask === "string" && typeMapping[block.mask]) {
          block.mask = typeMapping[block.mask];
        }
        newBlocks[blockKey] = block;
      });
      resolved.blocks = newBlocks;
    }
    return resolved;
  };

  const handleMaskAccept = (maskInstance) => () => {
    maskedValue.value = maskInstance.value;
  };

  const handleMaskComplete = (maskInstance) => () => {
    if (!isNil(maskInstance.typedValue)) {
      if (config.InputType === "number") {
        local.Value.value = maskInstance.typedValue ? Number(maskInstance.typedValue) : null;
      } else {
        local.Value.value = maskInstance.typedValue;
      }
    } else {
      local.Value.value = maskInstance.unmaskedValue;
    }
    maskedValue.value = maskInstance.value;
    handlersKeys.forEach((handler) => {
      if (handler.toLowerCase().includes("onchange")) {
        debouncedHandler(handlers[handler], null, local.Value.value);
      }
    });
  };

  const updateMaskValues = (maskInstance: InstanceType<typeof IMask.InputMask>) => {
    if (local.Value.value) {
      if (maskInstance.typedValue !== undefined) {
        maskInstance.typedValue = local.Value.value;
      } else {
        maskInstance.unmaskedValue = local.Value.value.toString();
      }
      maskedValue.value = maskInstance.value;
    }
  };

  useSignalEffect(() => {
    const formatName = local.Format.value;
    if (formatName && inputRef.current) {
      const format = GetFormatFromSite(formatName);
      if (format?.Mask) {
        const maskOptions = transformMaskOptions(format.Mask);
        const maskInstance = IMask(inputRef.current, maskOptions);
        maskRef.current = maskInstance;

        maskInstance.on("accept", handleMaskAccept(maskInstance));
        maskInstance.on("complete", handleMaskComplete(maskInstance));

        updateMaskValues(maskInstance);

        return () => maskInstance.destroy();
      } else if (format?.NumberFormat) {
        const { Prefix = "", Suffix = "", MaximumFractionDigits = 0, GroupSeparator = "," } = format.NumberFormat;
        const maskPattern = `${Prefix}num${Suffix}`;
        const maskOptions = {
          mask: maskPattern,
          blocks: {
            num: {
              mask: Number,
              scale: MaximumFractionDigits,
              padFractionalZeros: true,
              normalizeZeros: true,
              thousandsSeparator: GroupSeparator,
              radix: ".",
            },
          },
          lazy: false,
        };

        const maskInstance = IMask(inputRef.current, maskOptions);
        maskRef.current = maskInstance;

        maskInstance.on("accept", handleMaskAccept(maskInstance));
        maskInstance.on("complete", handleMaskComplete(maskInstance));

        updateMaskValues(maskInstance);

        return () => maskInstance.destroy();
      }
    }
  });

  useSignalEffect(() => {
    const mask = maskRef.current;
    const value = local.Value.value;

    if (!mask) return;

    if (isNil(value)) {
      mask.masked.reset();
      mask.masked.value = "";
      mask.unmaskedValue = "";
    } else {
      if (mask.typedValue !== undefined) {
        mask.typedValue = value;
      } else {
        mask.unmaskedValue = String(value);
      }
      maskedValue.value = mask.value;
    }

    mask.updateValue();
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    log.debug(tag, "onChange", e.target.value);
    const { value } = e.target;
    let v: any = value;
    if (!local.Format.value) {
      try {
        switch (local.InputType.value) {
          case "number": {
            const numericValue = toNumber(value);
            v = isNaN(numericValue) ? null : numericValue;
            local.Value.value = v;
            break;
          }
          case "date": {
            const dateValue = new Date(value);
            v = isNaN(dateValue.getTime()) ? null : dateValue;
            local.Value.value = v;
            break;
          }
          default:
            local.Value.value = value;
            break;
        }
      } catch (error) {
        log.error(tag, "Error parsing input value", error);
      }
    }
    handlersKeys.forEach((handler) => {
      if (handler.toLowerCase().includes("onchange")) {
        debouncedHandler(handlers[handler], e, v);
      }
    });
  };

  const inputType = local.Format.value ? "text" : local.InputType.value;

  const { currentConfig, currentContext } = createAutocompleteConfigAndContext(
    local.ContextAutocomplete.value,
    context,
    { elementRef: inputRef.current }
  );

  function transformCssToArray(css: string): string | null {
    try {
      // Parse the input to ensure it's valid JSON
      const parsedCss = JSON.parse(css);

      // Check if the parsed object is valid and is an object
      if (typeof parsedCss !== "object" || parsedCss === null || Array.isArray(parsedCss)) {
        throw new Error("Invalid JSON: Expected an object.");
      }

      // Transform the parsed object into the desired array format
      const transformed = Object.entries(parsedCss).map(([key, value]) => `"${key}":"${value}"`);
      return `[${transformed}]`;
    } catch (error) {
      error instanceof SyntaxError
        ? log.error(tag, "Invalid JSON input:", error.message)
        : log.error(tag, "Invalid JSON input");

      return null; // Return null or handle the error as needed
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (!config.TransformToString) return;

    log.debug(tag, "onPaste", e.clipboardData.getData("text"));

    const pastedData = e.clipboardData.getData("text");
    const modifiedData = transformCssToArray(pastedData);

    if (modifiedData == null) return;

    e.preventDefault(); // Prevent the default paste action

    local.Value.value = modifiedData;

    handlersKeys.forEach((handler) => {
      if (handler.toLowerCase().includes("onchange")) {
        debouncedHandler(handlers[handler], null, local.Value.value);
      }
    });
  };

  return (
    <>
      {local.Visible.value && (
        <TextField
          data-testid={getTestId(config)}
          data-type={config.__typename}
          inputRef={inputRef}
          sx={getsxObject(local.Style.value)}
          className={getClassName(local.Classes)}
          value={local.Format.value ? maskedValue.value ?? "" : local.Value.value ?? ""}
          type={inputType ?? "text"}
          error={!local.Valid.value.success}
          label={getCustomLabel(config.Label)}
          helperText={!local.Valid.value.success && local.Valid.value.reasons?.map((r) => r.message).join(". ")}
          autoComplete={config.AutoComplete as string}
          autoFocus={config.AutoFocus as boolean}
          color={config.Color as any}
          disabled={local.Disabled.value}
          fullWidth={config.FullWidth as boolean}
          margin={config.Margin as any}
          maxRows={local.MaxRows.value}
          minRows={local.MinRows.value}
          onPaste={handlePaste}
          rows={local.Rows.value}
          variant={config.Variant as any}
          multiline={local.Multiline.value}
          placeholder={config.Placeholder as string}
          onKeyDown={onKeyDown}
          required={config.Required as boolean}
          select={config.Select as boolean}
          size={config.Size as any}
          slotProps={{
            htmlInput: local.InputProps.value,
            input: local.inputProps.value,
          }}
          {...ignoreCustomHandlers(handlers)}
          onChange={handleChange}
          onFocus={(e) => {
            const [_, classes] = partitionMUIClasses(local.Classes.value);
            const action = publishUserAction(config, context, "input_focus", e, {
              Value: local.Value.value,
              EventCategory: "engagement",
              ElementClass: classes.join(" "),
            });
            handlers["onFocus"]?.(e, null, action);
          }}
          onBlur={(e) => {
            const [_, classes] = partitionMUIClasses(local.Classes.value);
            const action = publishUserAction(config, context, "input_blur", e, {
              Value: local.Value.value,
              EventCategory: "engagement",
              ElementClass: classes.join(" "),
            });
            handlers["onBlur"]?.(e, null, action);
          }}
        />
      )}
      {config.ContextAutocomplete && inputRef.current && (
        <NGLayoutItem config={currentConfig} context={currentContext} />
      )}
    </>
  );
}
